import { Field, HitStatus, QuickSearch, SearchVersion, User } from "aderant-conflicts-models";
import { Messages } from "components/Tile/Tiles.Messages";
import { splitToUserFriendlyString, titleCase } from "Functions/strings";
import _ from "lodash";
import React from "react";
import { conflictsPalette } from "styles/conflictsPalette";
import { StyledFieldOverrides } from "../DynamicFields/DisplayFields/commonStyles";
import { Tile } from "./Tile";
import { HitResult } from "pages/ResultsPage/HitResult";
import { UserChangePermissions } from "pages/ResultsPage/ResultsPage";

export type HitInformationTileProps = {
    user?: User;
    data?: HitResult;
    userChangePermissions: UserChangePermissions;
} & HitInformationTilePropsBySearchType;

type HitInformationTilePropsBySearchType =
    | {
          searchVersion?: QuickSearch;
          onHitChange?: never;
          searchReadOnly?: true;
      }
    | {
          searchVersion?: SearchVersion;
          onHitChange?: (status?: HitStatus, hitOwnerId?: string | null) => void;
          searchReadOnly?: boolean;
      };

// Constants
const DIVIDER_STYLE = {
    borderWidth: 0,
    borderStyle: "solid" as const,
    borderColor: conflictsPalette.divider,
    borderBottomWidth: "thin",
    margin: "0 0 7px 0"
};

const CONTAINER_STYLES = {
    marginBottom: "-6.5",
    marginTop: "0"
};

export const HitInformationTile = (props: HitInformationTileProps): JSX.Element => {
    const { data } = props;

    const fields: (Field & StyledFieldOverrides)[] = [];

    // Early return if no hit data is available
    if (!data?.hit?.hitLocations) {
        return <Tile title={Messages.HIT_INFORMATION_TILE_TITLE.getMessage()} fields={fields} />;
    }

    // Group hit locations by location and render fields
    _(data.hit.hitLocations)
        .groupBy((hitLocation) => hitLocation.location)
        .forEach((groupedLocations, location) => {
            // Add divider between groups (except for the first group)
            if (fields.length) {
                fields.push({
                    fieldLabel: "",
                    displayFormat: "Custom",
                    value: <hr style={DIVIDER_STYLE} />,
                    containerStyle: { marginBottom: CONTAINER_STYLES.marginBottom }
                });
            }

            // Add location, term, and text fields for this group
            fields.push(
                {
                    fieldLabel: Messages.HIT_LOCATION_LABEL.getMessage(),
                    displayFormat: "String",
                    value: `${titleCase(splitToUserFriendlyString(location)) ?? "-"}`,
                    ...(fields.length && { containerStyle: { marginTop: CONTAINER_STYLES.marginTop } })
                },
                {
                    fieldLabel: Messages.HIT_TERM_LABEL.getMessage(),
                    displayFormat: "String",
                    value: groupedLocations.map((l) => l.term).join("; ")
                },
                {
                    fieldLabel: Messages.HIT_TEXT_LABEL.getMessage(),
                    displayFormat: "Highlight",
                    value: groupedLocations.flatMap((l) => l.highlights)
                }
            );
        });

    return <Tile title={Messages.HIT_INFORMATION_TILE_TITLE.getMessage()} fields={fields} />;
};
